import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Spinner from '../Spinner';
import { InvestmentPackage } from '@/api/type';

const ChevronDownIcon = ({ className }: { className?: string }) => (
  <svg className={className || "w-4 h-4"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

interface PaymentSchedule {
  period: number;
  dueDate: string;
  paidDate?: string;
  amount: number;
  status: 'paid' | 'pending' | 'overdue';
}


interface MyInvestmentProps {
  investments?: InvestmentPackage[];
  loading?: boolean;
  showScheduleButton?: boolean;
  allowRedirect?: boolean;
}

// Investment Card Component
const InvestmentCard: React.FC<{ investment: InvestmentPackage; showScheduleButton?: boolean, allowRedirect?: boolean }> = ({ investment, showScheduleButton = true, allowRedirect = false }) => {
  const { t } = useTranslation();
  const [showSchedule, setShowSchedule] = useState(false);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [expandedYears, setExpandedYears] = useState<Record<number, boolean>>({});

  const toggleYear = (yearIndex: number) => {
    setExpandedYears(prev => ({
      ...prev,
      [yearIndex]: !prev[yearIndex]
    }));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN').format(amount);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('vi-VN');
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      paid: '!bg-green-50 !text-green-700 dark:!bg-green-900/20 dark:!text-green-400',
      approved: '!bg-green-50 !text-green-700 dark:!bg-green-900/20 dark:!text-green-400',
      active: '!bg-blue-50 !text-blue-700 dark:!bg-blue-900/20 dark:!text-blue-400',
      pending: '!bg-yellow-50 !text-yellow-700 dark:!bg-yellow-900/20 dark:!text-yellow-400',
      completed: '!bg-gray-50 !text-gray-700 dark:!bg-gray-700 dark:!text-gray-300',
      rejected: '!bg-red-50 !text-red-700 dark:!bg-red-900/20 dark:!text-red-400',
      registered: '!bg-purple-50 !text-purple-700 dark:!bg-purple-900/20 dark:!text-purple-400',
      cancelled: '!bg-gray-50 !text-gray-700 dark:!bg-gray-700 dark:!text-gray-300',
      expired: '!bg-red-50 !text-red-700 dark:!bg-red-900/20 dark:!text-red-400',
    };
    const labels: Record<string, string> = {
      paid: t('investment.status.paid', 'Đã thanh toán'),
      approved: t('investment.status.approved', 'Đã duyệt'),
      active: t('investment.status.active', 'Đang hoạt động'),
      pending: t('investment.status.pending', 'Chờ xử lý'),
      completed: t('investment.status.completed', 'Hoàn thành'),
      rejected: t('investment.status.rejected', 'Từ chối'),
      registered: t('investment.status.registered', 'Đã đăng ký'),
      cancelled: t('investment.status.cancelled', 'Đã hủy'),
      expired: t('investment.status.expired', 'Hết hạn'),
      
    };
    return (
      <span className={`px-2.5 py-1 text-xs font-medium rounded ${styles[status] || ''}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getPaymentStatusBadge = (status: PaymentSchedule['status']) => {
    const styles = {
      paid: '!bg-green-600 !text-white dark:!bg-green-500',
      pending: '!bg-yellow-500 !text-white dark:!bg-yellow-600',
      overdue: '!bg-red-500 !text-white dark:!bg-red-600',
    };
    const labels = {
      paid: t('investment.paymentStatus.paid', 'Đã thanh toán'),
      pending: t('investment.paymentStatus.pending', 'Chờ thanh toán'),
      overdue: t('investment.paymentStatus.overdue', 'Quá hạn'),
    };
    return (
      <span className={`px-2.5 py-1 text-xs font-medium rounded ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const groupedHistory = investment.profit_history.reduce((groups: any[][], item: any) => {
    const yearIndex = Math.floor((item.period - 1) / 12);
    if (!groups[yearIndex]) groups[yearIndex] = [];
    groups[yearIndex].push(item);
    return groups;
  }, []);

  return (
    <div className="!bg-white dark:!bg-gray-800 rounded-lg shadow-sm border !border-gray-200 dark:!border-gray-700">
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-lg font-semibold !text-gray-900 dark:!text-white">
                {investment.package_name}
              </h3>
              {getStatusBadge(investment.status_code)}
            </div>
            <p className="!text-gray-500 dark:!text-gray-400 text-sm">
              {investment.buy_date}
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold !text-primary">
              {formatCurrency(investment.total_invested)} {t('investment.currencyUnit', 'đ')}
            </p>
            <p className="!text-gray-500 dark:!text-gray-400 text-sm">
              {
                investment.package_name.toLowerCase().includes('trader') ? null : `${investment.total_months} ${t('investment.months', 'tháng')}`
              }
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
          <div className="!bg-gray-50 dark:!bg-gray-700/50 rounded-lg p-3">
            <p className="!text-gray-500 dark:!text-gray-400 text-xs mb-1">
              {investment.package_name.toLowerCase().includes('trader') ? t('investment.monthlyRevenue', 'Doanh thu hàng tháng') : t('investment.monthlyInterest', 'Thu nhập tối thiểu mỗi tháng')}
            </p>
            <p className="text-base font-semibold !text-gray-900 dark:!text-white">
              {formatCurrency(investment.monthly_profit)} {t('investment.currencyUnit', 'đ')}
            </p>
          </div>
          <div className={`${investment.package_name.toLowerCase().includes('trader') ? 'hidden' : ''} !bg-gray-50 dark:!bg-gray-700/50 rounded-lg p-3`}>
            <p className="!text-gray-500 dark:!text-gray-400 text-xs mb-1">
              {t('investment.interestRate', 'Lãi suất tối thiểu')}
            </p>
            <p className="text-base font-semibold !text-gray-900 dark:!text-white">
              {parseFloat(investment.interest_rate).toFixed(2)}%
            </p>
          </div>
          <div className="!bg-gray-50 dark:!bg-gray-700/50 rounded-lg p-3">
            <p className="!text-gray-500 dark:!text-gray-400 text-xs mb-1">
              {t('investment.progress', 'Tiến độ')}
            </p>
            <p className="text-base font-semibold !text-gray-900 dark:!text-white">
              {
                investment.package_name.toLowerCase().includes('trader') ? t('investment.noLimit', 'Không giới hạn') : `${investment.progress.paid_months} / ${investment.total_months} ${t('investment.months', 'tháng')}`
              }
            </p>
          </div>
        </div>

        {/* Payment Progress */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="!text-gray-600 dark:!text-gray-400 text-sm">
              {t('investment.paymentProgress', 'Tiến độ thanh toán')}
            </span>
            <span className="!text-gray-900 dark:!text-white font-medium text-sm">
              {Math.min(investment.progress.percent, 100)}% - {Number(investment.progress.received).toLocaleString()} {t('investment.currencyUnit', 'đ')}
              {investment.progress.percent < 100 && (
                <> | {t('investment.remaining', 'Còn lại')}: {Number(investment.total_invested - investment.progress.received).toLocaleString()} {t('investment.currencyUnit', 'đ')}</>
              )}
            </span>
          </div>
          <div className="w-full h-2 !bg-gray-200 dark:!bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full !bg-primary transition-all"
              style={{ width: `${investment.progress.percent}%` }}
            />
          </div>
        </div>
        {allowRedirect && !(investment.progress.percent >= 100) && (
          <div className="mt-4 text-right">
            <Link
              to={`/plans/${investment.package_id}/checkout?contractId=${investment.id}`}
              className="text-sm font-medium !text-primary hover:underline"
            >
              {t('investment.viewPaymentPage', 'Xem trang thanh toán')}
            </Link>
          </div>
        )}
        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 mt-6">
          {investment.payment_history?.length > 0 && (
            <button
              onClick={() => setShowPaymentHistory(!showPaymentHistory)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 !border !border-gray-300 dark:!border-gray-600 rounded !text-gray-700 dark:!text-gray-300 hover:!bg-gray-50 dark:hover:!bg-gray-700 transition-colors text-sm font-medium"
            >
              {t('investment.paymentHistory', 'Lịch sử thanh toán')}
              <ChevronDownIcon className={`w-4 h-4 transition-transform ${showPaymentHistory ? 'rotate-180' : ''}`} />
            </button>
          )}
          {showScheduleButton && !(investment.progress.percent < 100) && (
            <button
              onClick={() => setShowSchedule(!showSchedule)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 !border !border-gray-300 dark:!border-gray-600 rounded !text-gray-700 dark:!text-gray-300 hover:!bg-gray-50 dark:hover:!bg-gray-700 transition-colors text-sm font-medium"
            >
              {t('investment.interestSchedule', 'Lịch trả lãi')}
              <ChevronDownIcon className={`w-4 h-4 transition-transform ${showSchedule ? 'rotate-180' : ''}`} />
            </button>
          )}
        </div>
      </div>

      {/* Payment History Section */}
      {showPaymentHistory && (
        <div className="border-t !border-gray-200 dark:!border-gray-700 p-5 !bg-gray-50 dark:!bg-gray-900/50">
          <h4 className="text-sm font-semibold !text-gray-900 dark:!text-white mb-3">
            {t('investment.detailedPaymentHistory', 'Lịch sử thanh toán chi tiết')}
          </h4>
          <div className="space-y-3">
            {investment.payment_history.map((tx) => (
              <div
                key={tx.id}
                className="!bg-white dark:!bg-gray-800 rounded-lg w-full p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 !border !border-gray-100 dark:!border-gray-700 shadow-sm"
              >
                <div className="flex items-center gap-4">
                  <div className="flex flex-col items-center justify-center min-w-[3rem] h-12 rounded-xl !bg-gray-50 dark:!bg-gray-700/50 !border !border-gray-200 dark:!border-gray-600">
                    <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-sm font-semibold !text-gray-900 dark:!text-white mb-1">
                      {tx.transaction_code}
                    </div>
                    <div className="text-xs !text-gray-500 dark:!text-gray-400">
                      {formatDate(tx.created_at)}
                    </div>
                  </div>
                </div>

                <div className="flex-1 flex flex-row sm:items-center justify-between gap-1">
                  <div className="text-sm !text-gray-600 dark:!text-gray-400 mb-1">
                    {t('investment.adminNote', 'Ghi chú từ Admin')}:&nbsp;
                    <span className="text-xs italic">
                      ({tx.admin_note})
                    </span>
                  </div>
                  <div className="flex flex-col sm:items-end gap-1">
                    <div className="text-lg font-bold !text-primary">
                      {formatCurrency(tx.amount)} {t('investment.currencyUnit', 'đ')}
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(tx.status)}
                      {tx.image_url && (
                        <a
                          href={tx.image_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs !text-primary hover:underline font-medium"
                        >
                          {t('investment.viewReceipt', 'Xem minh chứng')}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payment Schedule */}
      {showScheduleButton && showSchedule && (
        <div className="border-t !border-gray-200 dark:!border-gray-700 p-5 !bg-gray-50 dark:!bg-gray-900/50">
          <h4 className="text-sm font-semibold !text-gray-900 dark:!text-white mb-3">
            {t('investment.detailedSchedule', 'Lịch trả lãi chi tiết')}
          </h4>
          <div className="space-y-6">
            {groupedHistory.map((yearGroup, index) => (
              <div key={index} className="space-y-3">
                <div
                  className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => toggleYear(index)}
                >
                  <div className="h-px flex-1 !bg-gray-200 dark:!bg-gray-700"></div>
                  <div className="flex items-center gap-2 px-2">
                    <span className="text-xs font-bold !text-gray-400 uppercase tracking-wider">
                      {t('investment.year', 'Năm')} {index + 1}
                    </span>
                    <ChevronDownIcon className={`w-3 h-3 !text-gray-400 transition-transform ${expandedYears[index] ? 'rotate-180' : ''}`} />
                  </div>
                  <div className="h-px flex-1 !bg-gray-200 dark:!bg-gray-700"></div>
                </div>

                {expandedYears[index] && (
                  <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-200">
                    {yearGroup.map((schedule) => (
                      <div
                        key={schedule.period}
                        className="!bg-white dark:!bg-gray-800 rounded-lg px-8 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 !border !border-gray-100 dark:!border-gray-700 shadow-sm"
                      >
                        <div className="flex items-center gap-4">
                          <div className="flex flex-col items-center justify-center w-12 h-12 rounded-xl !bg-gray-50 dark:!bg-gray-700/50 !border !border-gray-200 dark:!border-gray-600">
                            <span className="text-[10px] !text-gray-400 dark:!text-gray-500 font-bold uppercase leading-none mb-1">
                              {t('investment.periodShort', 'Kỳ')}
                            </span>
                            <span className="text-lg font-bold !text-primary leading-none">
                              {schedule.period}
                            </span>
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-semibold !text-gray-900 dark:!text-white">
                                {t('investment.dueDate', 'Đến hạn')}: {formatDate(schedule.dueDate)}
                              </span>
                            </div>
                            {schedule.paidDate && (
                              <div className="flex items-center gap-1.5">
                                <div className="w-1.5 h-1.5 rounded-full !bg-green-500" />
                                <p className="text-xs !text-green-600 dark:!text-green-400">
                                  {t('investment.paidDate', 'Đã trả')}: {formatDate(schedule.paidDate)}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex-1 md:px-8 !flex-row sm:flex items-center justify-between gap-4">
                          <div className="flex flex-row justify-evenly w-full space-y-1.5 border-t sm:border-t-0 sm:border-l !border-gray-100 dark:!border-gray-700 pt-3 sm:pt-0 sm:pl-8">
                            <div className="flex flex-col justify-between text-xs">
                              <span className="!text-gray-500 dark:!text-gray-400">{t('investment.taxableIncome', 'Thu nhập chịu thuế')}</span>
                              <span className="font-medium !text-gray-700 dark:!text-gray-300">{formatCurrency(schedule.gross_amount)} đ</span>
                            </div>
                            <div className="flex flex-col justify-between text-xs">
                              <span className="!text-gray-500 dark:!text-gray-400">{t('investment.taxPIT', 'Thuế TNCN (10%)')}</span>
                              <span className="font-medium !text-red-500">-{formatCurrency(schedule.tax_fee)} đ</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center justify-between sm:flex-col sm:items-end gap-2">
                          <div className="text-right">
                            <p className="text-[10px] uppercase font-bold !text-gray-400 dark:!text-gray-500 sm:mb-0.5 tracking-wider">
                              {t('investment.actualReceived', 'Thực nhận')}
                            </p>
                            <p className="text-lg font-bold !text-primary">
                              {formatCurrency(schedule.net_amount || schedule.amount)} {t('investment.currencyUnit', 'đ')}
                            </p>
                          </div>
                          {getPaymentStatusBadge(schedule.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const MyInvestment: React.FC<MyInvestmentProps> = ({
  investments = [],
  loading = false,
  showScheduleButton = true,
  allowRedirect = false
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size={40} />
      </div>
    );
  }

  if (investments.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 !bg-gray-100 dark:!bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg
            className="w-8 h-8 !text-gray-400 dark:!text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-semibold !text-gray-900 dark:!text-white mb-2">
          {t('investment.noInvestments', 'Chưa có khoản đầu tư nào')}
        </h3>
        <p className="!text-gray-500 dark:!text-gray-400 text-sm mb-6">
          {t('investment.startInvesting', 'Bắt đầu đầu tư để xem các gói đầu tư của bạn tại đây.')}
        </p>
        <Link to="/plans" className="px-6 py-2.5 !bg-primary hover:!bg-primary-dark !text-white rounded font-medium transition-colors">
          {t('investment.explore', 'Khám phá các gói đầu tư')}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {investments.map((investment) => (
        <InvestmentCard key={investment.id} investment={investment} showScheduleButton={showScheduleButton} allowRedirect={allowRedirect} />
      ))}
    </div>
  );
};

export default MyInvestment;
